import { Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '../_primitives';
import type { SlugAvailability } from '../slugAvailability';
import { AiSuggestButton } from './AiSuggestButton';

const AUTHOR_AUTOMATIC = '__automatic__';

/**
 * Slugify while the user is still typing: lowercase + illegal-char→dash only.
 * Deliberately does NOT collapse repeated dashes or trim leading/trailing
 * ones — a controlled input that strips the dash you just typed on every
 * keystroke makes it impossible to type "foo-bar" (Carl @47:18, meeting
 * 2026-07-16: typed dashes were silently rejected; paste worked because it
 * lands as one onChange, not one per character).
 */
function slugifyWhileTyping(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, '-');
}

/**
 * Slugify on commit (blur): collapse repeated dashes and trim leading/
 * trailing ones. Deferred here so the tidy-up never fights the user mid-type.
 */
function slugifyOnCommit(raw: string): string {
  return raw.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}

const ROBOTS_OPTIONS: Array<{ value: '' | 'index' | 'noindex'; label: string }> = [
  { value: '', label: 'Automatic' },
  { value: 'index', label: 'Index' },
  { value: 'noindex', label: 'Noindex' },
];

export interface PublishingSettingsCardProps {
  /** Canonical URL override. Empty = automatic permalink. */
  canonicalUrl: string;
  /** Robots/indexing override. Empty = automatic (QA workflow). */
  robotsOverride: '' | 'index' | 'noindex';
  /** WordPress author user ID override. Null = automatic author. */
  wordpressAuthorId: number | null;
  /** Site domain for the automatic-permalink placeholder. Falls back to 'example.com'. */
  siteDomain?: string;
  /** URL slug; editable when onSlugChange is provided, and used to build the automatic-permalink placeholder. */
  slug?: string;
  /** Optional WordPress author list. When provided + non-empty, the author field is a select. When absent/empty, falls back to a numeric ID input. */
  authors?: Array<{ id: number; name: string }>;
  onCanonicalUrlChange: (value: string) => void;
  onRobotsOverrideChange: (value: '' | 'index' | 'noindex') => void;
  onWordpressAuthorIdChange: (value: number | null) => void;
  /** When provided, renders an editable URL-slug input (auto-slugified). */
  onSlugChange?: (value: string) => void;
  keyword?: string;
  onKeywordChange?: (value: string) => void;
  /**
   * Read-only availability state from the rail's tenant-scoped live-typing
   * check. Advisory only — a `conflict` never disables saving; see
   * slugAvailability.ts canAutosaveSlugChange for the soft-guardrail
   * rationale (the publish-time slug_collision gate offers "Publish
   * anyway", and WordPress dedupes on write regardless).
   */
  slugAvailability?: SlugAvailability;
  /** Content ID for the Family F per-field slug suggest endpoint. */
  contentId?: string;
  /** Tenant ID for the Family F per-field slug suggest endpoint. */
  tenantId?: string;
  /** Disable the Suggest button while whole-run generation is in flight. */
  metadataGenerating?: boolean;
}

/**
 * Pure presentational rail card for the three "Advanced" publishing fields
 * ported from the classic SEOMetadataPanel: canonical URL, indexing override,
 * and WordPress author. No internal state, effects, or service calls.
 */
export function PublishingSettingsCard({
  canonicalUrl,
  robotsOverride,
  wordpressAuthorId,
  siteDomain,
  slug,
  authors,
  onCanonicalUrlChange,
  onRobotsOverrideChange,
  onWordpressAuthorIdChange,
  onSlugChange,
  keyword,
  onKeywordChange,
  slugAvailability = { status: 'idle' },
  contentId,
  tenantId,
  metadataGenerating,
}: PublishingSettingsCardProps) {
  const domain = siteDomain || 'example.com';
  const automaticPermalink = `https://${domain}/${slug || 'your-page'}/`;
  const hasAuthors = authors && authors.length > 0;
  const slugFeedbackId = 'publishing-slug-availability';

  return (
    <Card railSection="publishing">
      <CardHeader icon={<Settings2 className="size-3.5" />} title="Publishing" />

      <div className="px-3 pb-3">
        <div className="flex flex-col gap-3">
          {/* URL slug */}
          {onSlugChange && (
            <div>
              <div className="mb-1 flex items-center gap-1.5">
                <label
                  htmlFor="publishing-url-slug"
                  className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider"
                >
                  URL Slug
                </label>
                {contentId && tenantId && (
                  <AiSuggestButton
                    field="url_slug"
                    onSuggest={onSlugChange}
                    currentValue={slug}
                    contentId={contentId}
                    tenantId={tenantId}
                    disabled={metadataGenerating}
                    // URL stakes: accept/reject preview, never a silent fill.
                    preview
                  />
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[12.5px] text-muted-foreground">/</span>
                <input
                  id="publishing-url-slug"
                  type="text"
                  value={slug ?? ''}
                  onChange={(e) => onSlugChange(slugifyWhileTyping(e.target.value))}
                  onBlur={(e) => onSlugChange(slugifyOnCommit(e.target.value))}
                  placeholder="your-page-url"
                  aria-describedby={
                    slugAvailability.status === 'idle' ? undefined : slugFeedbackId
                  }
                  className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none transition-shadow focus:border-coral focus:ring-2 focus:ring-coral/20"
                />
              </div>
              {slugAvailability.status === 'checking' && (
                <p
                  id={slugFeedbackId}
                  className="mt-1 text-[10.5px] text-muted-foreground"
                  role="status"
                >
                  Checking existing site URLs…
                </p>
              )}
              {slugAvailability.status === 'available' && (
                <p
                  id={slugFeedbackId}
                  className="mt-1 text-[10.5px] text-teal-dark"
                  role="status"
                >
                  Available in the current site index.
                </p>
              )}
              {slugAvailability.status === 'conflict' && (
                <p
                  id={slugFeedbackId}
                  className="mt-1 text-[10.5px] text-coral-dark"
                  role="status"
                >
                  This URL slug is already in use
                  {slugAvailability.conflict?.title
                    ? ` by “${slugAvailability.conflict.title}”.`
                    : '.'}
                  {' '}WordPress will de-dupe it automatically if you publish anyway.
                </p>
              )}
              {slugAvailability.status === 'invalid' && (
                <p
                  id={slugFeedbackId}
                  className="mt-1 text-[10.5px] text-coral-dark"
                  role="status"
                >
                  Enter at least one letter or number for the URL slug.
                </p>
              )}
              {slugAvailability.status === 'error' && (
                <p
                  id={slugFeedbackId}
                  className="mt-1 text-[10.5px] text-muted-foreground"
                  role="status"
                >
                  Could not check current site URLs. This won't block saving.
                </p>
              )}
            </div>
          )}

          {/* Canonical URL — the canonical IS the slug. Visible, never
              editable: it live-mirrors the slug field above. The old override
              input is gone (spec: canonical follows the slug; the override was
              a mis-build carried from the advanced-fields port). If a legacy
              row still holds a stored override, show it honestly with a
              one-click clear — otherwise the override would stay silently
              active at publish while this field claims to follow the slug. */}
          <div>
            <div className="mb-1">
              <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                Canonical URL
              </span>
            </div>
            {canonicalUrl ? (
              <div data-testid="canonical-override-legacy">
                <p className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-warm-bg/60 text-foreground break-all">
                  {canonicalUrl}
                </p>
                <p className="text-[10.5px] text-muted-foreground mt-1">
                  Custom canonical set on this article.{' '}
                  <button
                    type="button"
                    onClick={() => onCanonicalUrlChange('')}
                    className="underline underline-offset-2 hover:text-foreground cursor-pointer"
                    data-testid="canonical-clear-override"
                  >
                    Clear it to follow the slug
                  </button>
                  .
                </p>
              </div>
            ) : (
              <div data-testid="canonical-derived">
                <p className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-warm-bg/60 text-muted-foreground break-all">
                  {automaticPermalink}
                </p>
                <p className="text-[10.5px] text-muted-foreground mt-1">
                  Follows the slug automatically.
                </p>
              </div>
            )}
          </div>

          {/* Indexing (robots) segmented control */}
          <div>
            <div className="mb-1">
              <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                Indexing
              </span>
            </div>
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {ROBOTS_OPTIONS.map((option) => {
                const active = robotsOverride === option.value;
                return (
                  <button
                    key={option.value || 'automatic'}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onRobotsOverrideChange(option.value)}
                    className={cn(
                      'px-3 py-1.5 text-[11.5px] font-medium transition-colors border-r border-border last:border-r-0',
                      active
                        ? 'bg-coral-light text-coral-dark'
                        : 'bg-card text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10.5px] text-muted-foreground mt-1">
              Automatic follows the QA workflow (drafts publish as noindex until approved).
            </p>
          </div>

          {/* Author */}
          <div>
            {hasAuthors ? (
              <>
                <div className="mb-1">
                  <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                    Author
                  </span>
                </div>
                <select
                  value={wordpressAuthorId != null ? String(wordpressAuthorId) : AUTHOR_AUTOMATIC}
                  onChange={(e) => {
                    const v = e.target.value;
                    onWordpressAuthorIdChange(v === AUTHOR_AUTOMATIC ? null : Number(v));
                  }}
                  className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none focus:border-coral focus:ring-2 focus:ring-coral/20"
                >
                  <option value={AUTHOR_AUTOMATIC}>Automatic</option>
                  {authors!.map((author) => (
                    <option key={author.id} value={String(author.id)}>
                      {author.name}
                    </option>
                  ))}
                </select>
                <p className="text-[10.5px] text-muted-foreground mt-1">
                  Leave on Automatic to use the automatic author.
                </p>
              </>
            ) : (
              <>
                <div className="mb-1">
                  <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                    WordPress author ID
                  </span>
                </div>
                <input
                  type="number"
                  inputMode="numeric"
                  value={wordpressAuthorId ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      onWordpressAuthorIdChange(null);
                      return;
                    }
                    const parsed = Number(raw);
                    onWordpressAuthorIdChange(Number.isNaN(parsed) ? null : parsed);
                  }}
                  placeholder="e.g. 4"
                  className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none focus:border-coral focus:ring-2 focus:ring-coral/20"
                />
                <p className="text-[10.5px] text-muted-foreground mt-1">
                  Leave empty to use the automatic author.
                </p>
              </>
            )}
          </div>

          {onKeywordChange && (
            <div>
              <div className="mb-1">
                <label
                  htmlFor="publishing-main-keyword"
                  className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider"
                >
                  Main Keyword
                </label>
              </div>
              <input
                id="publishing-main-keyword"
                type="text"
                value={keyword ?? ''}
                onChange={(e) => onKeywordChange(e.target.value)}
                placeholder="Focus keyword for this article"
                className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none transition-shadow focus:border-coral focus:ring-2 focus:ring-coral/20"
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
